/**
 * ContactsWidget Component
 *
 * Виджет для управления контактами организации.
 * Включает список контактов и функционал добавления/редактирования.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Stack,
  Avatar,
  Button,
  Box,
  Badge,
  ActionIcon,
  Menu,
  Anchor,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconUsers,
  IconPlus,
  IconPhone,
  IconMail,
  IconBrandTelegram,
  IconBrandWhatsapp,
  IconDots,
  IconPencil,
  IconTrash,
  IconStar,
} from '@tabler/icons-react';
import BaseWidget from './BaseWidget';
import ContactFormModal from './ContactFormModal';
import ContactViewModal from './ContactViewModal';
import tokens from '../../../theme/designTokens';
import { formatPhoneDisplay, formatPhoneForLink } from '../../../utils/phoneUtils';

/**
 * API функции для контактов
 */
const contactsApi = {
  async getContacts(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/contacts`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить контакты');
    }
    return response.json();
  },

  async createContact(organizationId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/contacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось создать контакт');
    }
    return response.json();
  },

  async updateContact(organizationId, contactId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/contacts/${contactId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось обновить контакт');
    }
    return response.json();
  },

  async deleteContact(organizationId, contactId) {
    const response = await fetch(`/api/organizations/${organizationId}/contacts/${contactId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить контакт');
    }
  },
};

/**
 * Форматирование полного имени
 */
const formatFullName = (contact) => {
  const parts = [];
  if (contact.last_name) parts.push(contact.last_name);
  if (contact.first_name) parts.push(contact.first_name);
  if (contact.middle_name) parts.push(contact.middle_name);
  return parts.join(' ') || 'Без имени';
};

/**
 * Получение инициалов
 */
const getInitials = (contact) => {
  const first = contact.first_name?.[0]?.toUpperCase() || '';
  const last = contact.last_name?.[0]?.toUpperCase() || '';
  return last + first || first || 'К';
};

/**
 * Компонент отображения контакта
 */
const ContactItem = ({ contact, onEdit, onDelete, onView }) => {
  const fullName = formatFullName(contact);
  const primaryPhone = contact.phones?.[0];

  return (
    <Group
      p="sm"
      style={{
        borderRadius: '12px',
        backgroundColor: tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = tokens.colors.neutral[2]}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = tokens.colors.neutral[1]}
      onClick={() => onView(contact)}
    >
      <Avatar size="md" radius="xl" color="blue">
        {getInitials(contact)}
      </Avatar>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} truncate>
            {fullName}
          </Text>
          {contact.is_primary && (
            <Badge
              size="xs"
              variant="filled"
              radius="xl"
              color="green"
              leftSection={<IconStar size={10} />}
            >
              Основной
            </Badge>
          )}
        </Group>

        {contact.position_name && (
          <Text size="xs" c="dimmed" truncate>
            {contact.position_name}
          </Text>
        )}

        {/* Контактная информация */}
        <Group gap="md" mt={4}>
          {primaryPhone && (
            <Anchor href={`tel:${formatPhoneForLink(primaryPhone.number)}`} size="xs" c="dimmed" onClick={(e) => e.stopPropagation()}>
              <Group gap={4}>
                <IconPhone size={12} />
                {formatPhoneDisplay(primaryPhone.number)}
                {contact.phones?.length > 1 && (
                  <Text span c="dimmed" size="xs">
                    (+{contact.phones.length - 1})
                  </Text>
                )}
              </Group>
            </Anchor>
          )}

          {contact.email && (
            <Anchor href={`mailto:${contact.email}`} size="xs" c="dimmed">
              <Group gap={4}>
                <IconMail size={12} />
                {contact.email}
              </Group>
            </Anchor>
          )}

          {contact.messengers?.telegram && (
            <Anchor
              href={contact.messengers.telegram.startsWith('@')
                ? `https://t.me/${contact.messengers.telegram.slice(1)}`
                : `https://t.me/${contact.messengers.telegram}`
              }
              target="_blank"
              size="xs"
              c="dimmed"
            >
              <Group gap={4}>
                <IconBrandTelegram size={12} />
                TG
              </Group>
            </Anchor>
          )}

          {contact.messengers?.whatsapp && (
            <Anchor
              href={`https://wa.me/${contact.messengers.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              size="xs"
              c="dimmed"
            >
              <Group gap={4}>
                <IconBrandWhatsapp size={12} />
                WA
              </Group>
            </Anchor>
          )}
        </Group>
      </Box>

      {/* Меню действий */}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          <Menu.Item
            leftSection={<IconPencil size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(contact);
            }}
          >
            Редактировать
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconTrash size={14} />}
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(contact);
            }}
          >
            Удалить
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};

/**
 * Основной компонент виджета
 */
const ContactsWidget = ({ organization, onActivityChange }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Модальное окно формы
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  // Модальное окно просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingContact, setViewingContact] = useState(null);

  // Загрузка контактов
  const loadContacts = useCallback(async () => {
    if (!organization?.id) return;

    try {
      setLoading(true);
      const data = await contactsApi.getContacts(organization.id);
      setContacts(data.items || []);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Открыть модальное окно для добавления
  const handleAddContact = () => {
    setEditingContact(null);
    setModalOpen(true);
  };

  // Открыть модальное окно для редактирования
  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setModalOpen(true);
  };

  // Открыть модальное окно для просмотра
  const handleViewContact = (contact) => {
    setViewingContact(contact);
    setViewModalOpen(true);
  };

  // Удалить контакт
  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Удалить контакт "${formatFullName(contact)}"?`)) {
      return;
    }

    try {
      await contactsApi.deleteContact(organization.id, contact.id);
      await loadContacts();
      // Обновляем историю активности
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to delete contact:', err);
      alert(err.message);
    }
  };

  // Сохранить контакт (создание или обновление)
  const handleSubmit = async (data) => {
    setActionLoading(true);
    try {
      if (editingContact) {
        await contactsApi.updateContact(organization.id, editingContact.id, data);
      } else {
        await contactsApi.createContact(organization.id, data);
      }
      setModalOpen(false);
      setEditingContact(null);
      await loadContacts();
      // Обновляем историю активности
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      throw err; // Пробросим ошибку в модальное окно
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <BaseWidget
        title="Контакты"
        icon={IconUsers}
        count={contacts.length}
        collapsible={true}
        defaultExpanded={contacts.length > 0}
        actions={
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              handleAddContact();
            }}
          >
            Добавить
          </Button>
        }
      >
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : contacts.length > 0 ? (
          <Stack gap="xs">
            {contacts.map((contact) => (
              <ContactItem
                key={contact.id}
                contact={contact}
                onEdit={handleEditContact}
                onDelete={handleDeleteContact}
                onView={handleViewContact}
              />
            ))}
          </Stack>
        ) : (
          <Box py="lg" style={{ textAlign: 'center' }}>
            <IconUsers
              size={40}
              style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
            />
            <Text size="sm" c="dimmed">
              Контакты не добавлены
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              Добавьте контактных лиц организации
            </Text>
            <Button
              variant="light"
              size="xs"
              mt="md"
              leftSection={<IconPlus size={14} />}
              onClick={handleAddContact}
            >
              Добавить контакт
            </Button>
          </Box>
        )}
      </BaseWidget>

      {/* Модальное окно формы */}
      <ContactFormModal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingContact(null);
        }}
        onSubmit={handleSubmit}
        contact={editingContact}
        organizationId={organization?.id}
        loading={actionLoading}
      />

      {/* Модальное окно просмотра */}
      <ContactViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingContact(null);
        }}
        contact={viewingContact}
        onEdit={handleEditContact}
      />
    </>
  );
};

export default ContactsWidget;
